import { usePostHog } from 'posthog-js/react';
import { useLocation } from 'react-router-dom';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { Environment, PATH, TESTING_SHOP } from '@/constants';
import { convertQueryStringToObject } from '@/helpers';
import { AccountPlan } from '@/constants/enum';
import { PLAN_DISPLAY_NAME, PlanDisplayName } from '@/constants/plan';

export type TriggerType = 'feature_gate' | 'paywall' | 'nav_link' | 'banner';
export type EntryType = 'onboarding' | 'feature_gate' | 'direct' | 'nav_link' | 'email_promo';

export const ENTRY_TYPE_MAP: Record<TriggerType, EntryType> = {
  nav_link: 'nav_link',
  feature_gate: 'feature_gate',
  paywall: 'feature_gate',
  banner: 'feature_gate',
};

export const PLAN_NAME_MAP: Partial<Record<AccountPlan, PlanDisplayName>> = {
  [AccountPlan.Basic]: PLAN_DISPLAY_NAME[AccountPlan.Basic],
  [AccountPlan.Professional]: PLAN_DISPLAY_NAME[AccountPlan.Professional],
  [AccountPlan.Enterprise]: PLAN_DISPLAY_NAME[AccountPlan.Enterprise],
};

export interface NavigateToPlanArgs {
  trigger_type: TriggerType;
  trigger_element: string;
  feature: string;
}

export const useUpgradeTracking = () => {
  const posthog = usePostHog();
  const location = useLocation();
  const navigate = useConfirmationNavigate();

  const search = convertQueryStringToObject(window.location.search);
  const isOpenFromCms = search?.scope === 'omegatheme';
  const isProduction = process.env.REACT_APP_ENV === Environment.Production;
  const isTestingShop = TESTING_SHOP.includes(search?.shop || '');
  const isGatePassed = isProduction && !isOpenFromCms && !isTestingShop;

  const captureEvent = (name: string, props: Record<string, unknown>) => {
    if (!posthog || !isGatePassed) return;
    posthog.capture(name, props);
  };

  const navigateToPlan = ({ trigger_type, trigger_element, feature }: NavigateToPlanArgs) => {
    captureEvent('upgrade_cta_clicked', {
      source_page: location.pathname,
      trigger_element,
      trigger_type,
      feature,
    });
    navigate(PATH.PLAN, {
      state: {
        source_page: location.pathname,
        entry_type: ENTRY_TYPE_MAP[trigger_type],
        feature,
      },
    });
  };

  return { captureEvent, navigateToPlan };
};
